import type { Component } from 'solid-js';

export type AppIconName =
  | 'approvals'
  | 'bell'
  | 'basket'
  | 'child-chores'
  | 'child-goals'
  | 'child-history'
  | 'child-me'
  | 'child-money'
  | 'child-settings'
  | 'child-sign-out'
  | 'deposit'
  | 'feedback'
  | 'gift'
  | 'goals'
  | 'history'
  | 'home'
  | 'kids'
  | 'lock'
  | 'money'
  | 'person'
  | 'routines'
  | 'settings'
  | 'sign-out'
  | 'sparkles'
  | 'switch';

type Props = {
  name: AppIconName;
  class?: string;
  title?: string;
};

const IconPaths: Component<{ name: AppIconName }> = (props) => {
  // Keep this untyped: SVG attributes for <svg> and <path> have different
  // `ref` element types, and sharing the broader svg attribute type makes
  // Solid reject the spread under exactOptionalPropertyTypes.
  const common = {
    fill: 'none',
    stroke: 'currentColor',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
    'stroke-width': 1.9,
  } as const;

  switch (props.name) {
    case 'home':
      return <path {...common} d="m3 10 9-7 9 7v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z" />;
    case 'approvals':
      return <path {...common} d="M5 3h14v18H5zM8 8l2.2 2.2L16 5.5M8 15h8" />;
    case 'kids':
      return <path {...common} d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM5 21a7 7 0 0 1 14 0" />;
    case 'basket':
      return (
        <path {...common} d="M4 9h16l-1.4 11H5.4ZM8 9l1.5-4h5L16 9M8 13h.01M12 13h.01M16 13h.01" />
      );
    // Child navigation uses a friendlier, rounder set of SVGs. They are
    // still token-aware SVGs (rather than platform emoji), so Android,
    // FireOS and iOS show the same playful artwork.
    case 'child-chores':
      return (
        <>
          <path {...common} d="M4 10h16l-1.5 10.5H5.5ZM8 10l1.7-4h4.6l1.7 4" />
          <path {...common} d="M8.5 14h.01M12 14h.01M15.5 14h.01M10 17h4" />
          <path {...common} d="m18 4 .5 1.7L20 6.2l-1.5.5L18 8.5l-.5-1.8-1.5-.5 1.5-.5Z" />
        </>
      );
    case 'child-money':
      return (
        <>
          <path
            {...common}
            d="M4 7h16v11H4zM8 12h.01M16 12h.01M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8"
          />
          <path
            {...common}
            d="M12 10v4M10.6 11.2c.3-.8 2.5-1 2.8.1.4 1.5-3 1-2.8 2.2.2 1 2.4 1 2.8.1"
          />
        </>
      );
    case 'child-goals':
      return (
        <>
          <circle {...common} cx="12" cy="12" r="7.5" />
          <circle {...common} cx="12" cy="12" r="3" />
          <path {...common} d="m18 4 .5 1.6 1.6.5-1.6.5L18 8.2l-.5-1.6-1.6-.5 1.6-.5Z" />
        </>
      );
    case 'child-me':
      return (
        <>
          <circle {...common} cx="12" cy="12" r="8" />
          <path {...common} d="M9 11h.01M15 11h.01M9.5 15c1.4 1.2 3.6 1.2 5 0" />
          <path {...common} d="m5 5 .4 1.2L6.6 6.6l-1.2.4L5 8.2 4.6 7 3.4 6.6l1.2-.4Z" />
        </>
      );
    case 'child-history':
      return (
        <>
          <path {...common} d="M5 4.5h11a3 3 0 0 1 3 3V20H8a3 3 0 0 0-3 1Z" />
          <path {...common} d="M5 4.5V21M9 9h6M9 13h6M9 17h4" />
        </>
      );
    case 'child-settings':
      return (
        <path
          {...common}
          d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm0-5 1.1 2.2 2.4.4 1.7-1.7 1.8 1.8-1.7 1.7.4 2.4L21 11v2l-2.2 1.1-.4 2.4 1.7 1.7-1.8 1.8-1.7-1.7-2.4.4L13 21h-2l-1.1-2.2-2.4-.4-1.7 1.7-1.8-1.8 1.7-1.7-.4-2.4L3 13v-2l2.2-1.1.4-2.4L3.9 5.8 5.7 4l1.7 1.7 2.4-.4L11 3Z"
        />
      );
    case 'child-sign-out':
      return <path {...common} d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" />;
    case 'money':
      return (
        <path {...common} d="M4 6h16v12H4zM8 12h.01M16 12h.01M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
      );
    case 'goals':
      return (
        <path
          {...common}
          d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0-3a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"
        />
      );
    case 'sparkles':
      return (
        <path
          {...common}
          d="m12 3 .9 4.1L17 8l-4.1.9L12 13l-.9-4.1L7 8l4.1-.9ZM19 14l.5 2.5L22 17l-2.5.5L19 20l-.5-2.5L16 17l2.5-.5ZM5 15l.6 2.4L8 18l-2.4.6L5 21l-.6-2.4L2 18l2.4-.6Z"
        />
      );
    case 'history':
      return <path {...common} d="M4 4h16v16H4zM8 8h8M8 12h8M8 16h5" />;
    case 'settings':
      return (
        <path
          fill="currentColor"
          fill-rule="evenodd"
          d="M19.43 12.98c.04-.32.07-.65.07-.98s-.02-.66-.07-.98l2.11-1.65a.5.5 0 0 0 .12-.64l-2-3.46a.5.5 0 0 0-.6-.22l-2.49 1a7.6 7.6 0 0 0-1.69-.98L14.5 2.42A.5.5 0 0 0 14 2h-4a.5.5 0 0 0-.5.42l-.38 2.65c-.61.25-1.18.59-1.69.98l-2.49-1a.5.5 0 0 0-.6.22l-2 3.46a.5.5 0 0 0 .12.64l2.11 1.65c-.05.32-.08.65-.08.98s.03.66.08.98l-2.11 1.65a.5.5 0 0 0-.12.64l2 3.46a.5.5 0 0 0 .6.22l2.49-1c.51.4 1.08.73 1.69.98l.38 2.65c.04.24.25.42.5.42h4a.5.5 0 0 0 .5-.42l.38-2.65c.61-.25 1.17-.58 1.69-.98l2.49 1a.5.5 0 0 0 .6-.22l2-3.46a.5.5 0 0 0-.12-.64l-2.11-1.65ZM12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        />
      );
    case 'switch':
      return <path {...common} d="M7 7h11l-3-3M18 17H7l3 3M18 7l3 3-3 3M6 14l-3-3 3-3" />;
    case 'sign-out':
      return <path {...common} d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" />;
    case 'bell':
      return <path {...common} d="M18 10a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9ZM10 22h4" />;
    case 'lock':
      return <path {...common} d="M6 10V7a6 6 0 0 1 12 0v3M5 10h14v11H5zM12 14v3" />;
    case 'person':
      return <path {...common} d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM4 21a8 8 0 0 1 16 0" />;
    case 'routines':
      return (
        <>
          <rect {...common} x="4" y="5" width="16" height="15" rx="2" />
          <path
            {...common}
            d="M8 3v4M16 3v4M4 10h16M8 14h.01M12 14h.01M16 14h.01M8 17h.01M12 17h.01"
          />
        </>
      );
    case 'gift':
      return (
        <path
          {...common}
          d="M4 10h16v11H4zM3 7h18v3H3zM12 7v14M8 7a2 2 0 1 1 4-2c0 2-4 2-4 2Zm8 0a2 2 0 1 0-4-2c0 2 4 2 4 2Z"
        />
      );
    case 'deposit':
      return <path {...common} d="M4 7h16v12H4zM12 17V9M8.5 12.5 12 9l3.5 3.5" />;
    case 'feedback':
      return <path {...common} d="M4 5h16v12H9l-5 4ZM8 9h8M8 13h5" />;
  }
};

/** A small, token-aware icon set. SVGs inherit `currentColor`, unlike emoji. */
export const AppIcon: Component<Props> = (props) => (
  <svg
    class={`app-icon${props.class ? ` ${props.class}` : ''}`}
    data-icon={props.name}
    viewBox="0 0 24 24"
    aria-hidden={props.title ? undefined : 'true'}
    aria-label={props.title}
    role={props.title ? 'img' : undefined}
  >
    {props.title ? <title>{props.title}</title> : null}
    <IconPaths name={props.name} />
  </svg>
);
